import type { ChatMessage } from './ChatBot';

/** Strip markdown to plain text while preserving inline link URLs. */
export function markdownToPlainText(markdown: string): string {
  let text = markdown;

  // [label](url) -> label (url)
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label: string, url: string) => {
    const trimmedLabel = label.trim();
    const trimmedUrl = url.trim();
    if (!trimmedLabel) return trimmedUrl;
    if (trimmedLabel === trimmedUrl) return trimmedUrl;
    return `${trimmedLabel} (${trimmedUrl})`;
  });

  // Bare URLs in angle brackets or autolinks
  text = text.replace(/<((?:https?:\/\/)[^>]+)>/g, '$1');

  // Remove remaining markdown syntax (headings, emphasis, lists)
  text = text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '');

  return text.trim();
}

export interface GenerateTranscriptInput {
  institutionName: string;
  agentName: string;
  messages: ChatMessage[];
  /** Used for header session date and filename. Defaults to first message date or today. */
  sessionDate?: Date;
}

export function formatTranscriptTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString();
}

export function formatTranscriptSessionDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatTranscriptFilename(institutionName: string, sessionDate: Date): string {
  const yyyy = sessionDate.getFullYear();
  const mm = String(sessionDate.getMonth() + 1).padStart(2, '0');
  const dd = String(sessionDate.getDate()).padStart(2, '0');
  const safeName = institutionName.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
  return `${safeName || 'Institution'}-chat-${yyyy}-${mm}-${dd}.txt`;
}

/** Messages with non-empty text only — excludes bot placeholders before stream chunks arrive. */
export function getCompletedTranscriptMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.filter((msg) => Boolean(msg.text?.trim()));
}

export function canShowTranscriptDownload(enabled: boolean): boolean {
  return enabled;
}

export function generateTranscriptText(input: GenerateTranscriptInput): string {
  const completed = getCompletedTranscriptMessages(input.messages);
  const sessionDate =
    input.sessionDate ??
    (completed.length > 0 ? new Date(completed[0].timestamp) : new Date());

  const header = [
    input.institutionName,
    'Conversation transcript',
    formatTranscriptSessionDate(sessionDate),
    '',
    '---',
    '',
  ].join('\n');

  const body = completed
    .map((msg) => {
      const sender = msg.role === 'user' ? 'You' : input.agentName;
      const time = formatTranscriptTimestamp(msg.timestamp);
      const text = markdownToPlainText(msg.text);
      return `${sender} (${time})\n${text}`;
    })
    .join('\n\n');

  return `${header}${body}\n`;
}

export function buildTranscriptDownload(input: GenerateTranscriptInput): {
  content: string;
  filename: string;
} {
  const completed = getCompletedTranscriptMessages(input.messages);
  const sessionDate =
    input.sessionDate ??
    (completed.length > 0 ? new Date(completed[0].timestamp) : new Date());

  return {
    content: generateTranscriptText(input),
    filename: formatTranscriptFilename(input.institutionName, sessionDate),
  };
}
